import React, { useContext, useMemo, useState } from "react";
import { Box, Button, Grid, Stack, TextField, Typography } from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router";
import { string } from "prop-types";
import { useTextDomainContext, useUser } from "@/app/providers";
import {
  CustomerDropdown,
  DocumentTypeDropdown,
  Dropzone2,
  EnergyTypeDropdown,
  FilterBox,
  SingleDatePicker,
  SubsidiaryDropdown,
  TenantDropdown,
} from "@/components";
import {
  useCompanies,
  useGetGroups,
  useUpdateUserDocument,
  useUploadUserDocument,
} from "@/hooks";
import { useSnackbar } from "notistack";
import { formatDateToISOWithoutTimezone } from "@/utils";

type AddDocumentProps = {
  parentPath?: string;
};

type ValueLike = string | { value?: string | number | null } | null | undefined;

export const AddDocument = (props: AddDocumentProps) => {
  const location = useLocation();
  // Extract row from nested structure: location.state = {row: {...}}
  const row = location?.state?.row || location?.state;
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const { user } = useUser();
  const { currentSubsidiaryId, currentTeamId } = user || {};
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const parentPath = props.parentPath ? `/${props.parentPath}` : "";

  const isEditMode = !!row?.id;
  const selectedValues = {
    Id: row?.id || "",
    name: row?.name || "",
    description: row?.description || "",
    tenant: row?.tenantId || "",
    customer: row?.parentId || "",
    subsidiary: row?.childId || "",
    energyType: row?.energyType || "",
    startDate: row?.startDate || null,
    endDate: row?.endDate || null,
    documentType: row?.documentType || "",
  };
  const accept = {
    "application/x-pdf": [".pdf"],
  };

  //#region useState
  const [newName, setNewName] = useState(null);
  const [newDescription, setNewDescription] = useState(null);
  const [newTenant, setNewTenant] = useState(null);
  // Initialize customer and subsidiary to null for new documents, not to context values
  // This prevents defaulting to context when user wants "all"
  const [newCustomer, setNewCustomer] = useState(null);
  const [newSubsidiary, setNewSubsidiary] = useState(null);
  const [newEnergyType, setNewEnergyType] = useState(null);
  // const [newBuilding, setNewBuilding] = useState(null);
  // const [newMeter, setNewMeter] = useState(null);
  const [newStartDate, setNewStartDate] = useState(null);
  const [newEndDate, setNewEndDate] = useState(null);
  // const [newCurrency, setNewCurrency] = useState(null);
  const [newDocumentType, setNewDocumentType] = useState(null);
  const [files, setFiles] = useState([]);
  //#endregion useState

  const { uploadDocument, loading: uploadLoading } = useUploadUserDocument();
  const { updateDocument, loading: updateLoading } = useUpdateUserDocument();

  const loading = uploadLoading || updateLoading;

  //#region useMemo
  const name = useMemo(() => {
    return newName || selectedValues.name || "";
  }, [newName, selectedValues.name]);

  const description = useMemo(() => {
    return newDescription || selectedValues.description || "";
  }, [newDescription, selectedValues.description]);

  const tenant = useMemo(() => {
    return newTenant || selectedValues.tenant || "";
  }, [newTenant, selectedValues.tenant]);

  const customer = useMemo(() => {
    // If user has made a new selection, use it (even if it's "all")
    if (newCustomer !== null) {
      return newCustomer;
    }
    // If editing, use the saved value
    if (selectedValues.customer) {
      return selectedValues.customer;
    }
    // Otherwise use current context or default to "all"
    return currentTeamId || "all";
  }, [currentTeamId, newCustomer, selectedValues.customer]);

  // Get subsidiaries to determine if we should show "All" option
  const { customers: allCompanies } = useCompanies({
    customerId: customer?.value || customer,
  });

  const parentTeam = useMemo(() => {
    return allCompanies[0]?.name || "";
  }, [allCompanies]);

  const { subsidiary: subsidiaries } = useGetGroups({
    customerName: parentTeam,
  });

  // Only show "All" option when there are 2 or more subsidiaries
  const showAllSubsidiaries = useMemo(() => {
    return subsidiaries?.length > 1;
  }, [subsidiaries]);

  const subsidiary = useMemo(() => {
    // Determine base selection
    let resolved;
    if (newSubsidiary !== null) {
      resolved = newSubsidiary;
    } else if (isEditMode && selectedValues.subsidiary) {
      resolved = selectedValues.subsidiary;
    } else {
      resolved = currentSubsidiaryId || "all";
    }

    const getValue = (v: ValueLike) => {
      if (v == null) return null;
      if (typeof v === "object" && v.value !== undefined) return v.value;
      return v;
    };

    const resolvedVal = getValue(resolved);

    // If subsidiaries list is available, ensure resolved matches one of the options
    if (Array.isArray(subsidiaries) && subsidiaries.length > 0) {
      const match = subsidiaries.find((opt) => {
        const optVal = opt?.id ?? opt?.label ?? opt;
        return String(optVal) === String(resolvedVal);
      });

      if (match) {
        // Return in the same form as resolved (object or primitive) when matched
        return resolved;
      }

      // No match -> return first option's value (prefer `.value` then `.id` then the item itself)
      const first = subsidiaries[0];
      return first?.id ?? first?.label ?? first;
    }

    // No subsidiaries available -> return resolved (may be null or "all")
    return resolved;
  }, [
    currentSubsidiaryId,
    isEditMode,
    newSubsidiary,
    selectedValues.subsidiary,
    subsidiaries,
  ]);

  const energyType = useMemo(() => {
    return newEnergyType || selectedValues.energyType || "electricity";
  }, [newEnergyType, selectedValues.energyType]);

  const startDate = useMemo(() => {
    if (newStartDate) return newStartDate;
    if (selectedValues.startDate) {
      return selectedValues.startDate instanceof Date
        ? selectedValues.startDate
        : new Date(selectedValues.startDate);
    }
    return new Date();
  }, [newStartDate, selectedValues.startDate]);

  const endDate = useMemo(() => {
    if (newEndDate) return newEndDate;
    if (selectedValues.endDate) {
      return selectedValues.endDate instanceof Date
        ? selectedValues.endDate
        : new Date(selectedValues.endDate);
    }
    return new Date();
  }, [newEndDate, selectedValues.endDate]);

  const documentType = useMemo(() => {
    return newDocumentType || selectedValues.documentType || "";
  }, [newDocumentType, selectedValues.documentType]);

  //#endregion useMemo

  //#region Handlers
  const submitHandler = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      // For new documents, file is required
      if (!isEditMode && (!files || files.length === 0)) {
        enqueueSnackbar(gettext("Please select a file to upload"), {
          variant: "warning",
        });
        return;
      }

      const file = files && files.length > 0 ? files[0] : null;

      // Extract values, handling both object format {value: "x"} and string format "x"
      const extractValue = (val: ValueLike) => {
        if (!val) return "all";
        if (typeof val === "object" && val.value !== undefined)
          return val.value;
        return val;
      };

      const documentData = {
        tenantId: String(extractValue(tenant)),
        parentId: String(extractValue(customer)),
        childId: String(extractValue(subsidiary)),
        energyType: String(extractValue(energyType)),
        name: name,
        description: description,
        startDate: formatDateToISOWithoutTimezone(startDate),
        endDate: formatDateToISOWithoutTimezone(endDate),
        documentType: String(extractValue(documentType)),
      };

      let result;

      if (isEditMode) {
        // Update existing document
        result = await updateDocument({
          documentId: selectedValues.Id,
          file: file, // Optional - only if user wants to replace the file
          ...documentData,
        });

        if (result && result.success) {
          enqueueSnackbar(gettext("Document updated successfully"), {
            variant: "success",
          });
          navigate(`${parentPath}/documents`);
        }
      } else {
        // Upload new document
        result = await uploadDocument({
          file: file,
          ...documentData,
        });

        if (result && result.success) {
          enqueueSnackbar(gettext("Document uploaded successfully"), {
            variant: "success",
          });
          navigate(`${parentPath}/documents`);
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      enqueueSnackbar(
        msg || gettext(isEditMode ? "Update failed" : "Upload failed"),
        {
          variant: "error",
        },
      );
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewName(e.target.value);
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewDescription(e.target.value);
  };

  //#endregion Handlers

  // console.log("tenant: ", tenant);
  // console.log("customer: ", customer);
  // console.log("subsidiary: ", subsidiary);

  return (
    <>
      <Box
        sx={{
          width: "auto",
        }}
      >
        <form onSubmit={submitHandler}>
          <Stack
            spacing={3}
            sx={{
              height: "100%",
            }}
          >
            <Grid
              container
              spacing={0}
              sx={{
                width: "100%",
              }}
            >
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="h4">
                  {!selectedValues.Id && gettext("Upload New Document")}
                  {selectedValues.Id && gettext("Edit Document Details")}
                </Typography>
              </Grid>
              <Grid
                size={{ xs: 12, sm: 6 }}
                sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}
                // alignItems={"center"}
              >
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{ minWidth: 150 }}
                >
                  {loading
                    ? isEditMode
                      ? gettext("Updating...")
                      : gettext("Uploading...")
                    : gettext("Save")}
                </Button>
                <Button
                  variant="outlined"
                  to={`${parentPath}/documents`}
                  component={Link}
                >
                  {gettext("Document list")}
                </Button>
              </Grid>

              <Box
                sx={{
                  height: "100%",
                }}
              >
                <FilterBox>
                  <Grid
                    container
                    spacing={2}
                    sx={{
                      width: "100%",
                    }}
                  >
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TenantDropdown
                        required
                        defaultValue={tenant}
                        onChange={setNewTenant}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <CustomerDropdown
                        required
                        // showAll
                        defaultValue={customer}
                        onChange={setNewCustomer}
                        componentLabel={gettext("Parent Company")}
                        currentTenantId={tenant?.value || tenant}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <SubsidiaryDropdown
                        defaultValue={
                          isEditMode
                            ? selectedValues.subsidiary
                            : currentSubsidiaryId || "all"
                        }
                        showAll={showAllSubsidiaries}
                        onChange={setNewSubsidiary}
                        componentLabel={gettext("Child Company")}
                        currentTeamId={customer?.value || customer}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <EnergyTypeDropdown
                        onChange={setNewEnergyType}
                        defaultValue={energyType}
                        showAll={true}
                      />
                    </Grid>
                    {/*<Grid desc="Building dropdown" size={{xs: 12, md: 4}}>*/}
                    {/*  <BuildingDropdown*/}
                    {/*    onChange={setNewBuilding}*/}
                    {/*    defaultValue={building}*/}
                    {/*    showAll={true}*/}
                    {/*  />*/}
                    {/*</Grid>*/}
                    {/*<Grid size={{xs: 12, md: 4}}>*/}
                    {/*  <MeterDropdown*/}
                    {/*    onChange={setNewMeter}*/}
                    {/*    energyType={energyType}*/}
                    {/*    building={building}*/}
                    {/*    defaultValue={meter}*/}
                    {/*    showAll={true}*/}
                    {/*  />*/}
                    {/*</Grid>*/}
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        type="text"
                        label={gettext("Name")}
                        placeholder={gettext("Enter name")}
                        size="small"
                        required
                        fullWidth
                        value={name}
                        onChange={handleNameChange}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        type="text"
                        label={gettext("Description")}
                        placeholder={gettext("Enter description")}
                        size="small"
                        fullWidth
                        value={description}
                        onChange={handleDescriptionChange}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 4 }}>
                      <DocumentTypeDropdown
                        defaultValue={documentType}
                        onChange={setNewDocumentType}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 4 }}>
                      <SingleDatePicker
                        componentLabel={gettext("Start Date")}
                        selectedDate={startDate.toString()}
                        user={user}
                        onValueChange={setNewStartDate}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 4 }}>
                      <SingleDatePicker
                        componentLabel={gettext("End Date")}
                        selectedDate={endDate.toString()}
                        user={user}
                        onValueChange={setNewEndDate}
                      />
                    </Grid>

                    {/*<Grid size={{xs: 12, md: 4}}>*/}
                    {/*  <CurrencyDropdown*/}
                    {/*    defaultValue={currency}*/}
                    {/*    onChange={setNewCurrency}*/}
                    {/*    showAll={false}*/}
                    {/*  />*/}
                    {/*</Grid>*/}

                    <Grid size={{ xs: 12 }}>
                      <Dropzone2
                        accept={accept}
                        componentLabel={
                          isEditMode
                            ? gettext("Upload New Document File (Optional)")
                            : gettext("Upload Document Files")
                        }
                        description={
                          isEditMode
                            ? gettext(
                                "Only PDF files are allowed. Leave empty to keep the existing file.",
                              )
                            : gettext("Only PDF files are allowed.")
                        }
                        getSelected={setFiles}
                        // progress={progress}
                      />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        sx={{ minWidth: 150 }}
                      >
                        {loading
                          ? isEditMode
                            ? gettext("Updating...")
                            : gettext("Uploading...")
                          : gettext("Save")}
                      </Button>
                    </Grid>
                  </Grid>
                </FilterBox>
              </Box>
            </Grid>
          </Stack>
        </form>
      </Box>
    </>
  );
};

AddDocument.propTypes = {
  parentPath: string,
};
